import { Delete as DeleteIcon, Save as SaveIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Grid2, TextField, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { IInterceptorRowDetails } from ".";
import { AGDialog } from "../../../components/AGDialog";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import { useNotification } from "../../../components/FeedbackNotification/useNotification";
import {
	useCreateInterceptorMutation,
	useDeleteInterceptorMutation,
	useEditInterceptorMutation
} from "../../../redux/reducers/configuration.reducer";
import { VALIDATION_REGEX } from "../../../utils/constants";
import { useAppSelector } from "../../../redux";

interface IInterceptorDetailsDialogProps {
	open: boolean;
	interceptorDetails: IInterceptorRowDetails | null;
	onClose: () => void;
}

interface IInterceptorFormInputs {
	location_name: string;
	number_plate: string;
	uuid: string;
}

const InterceptorDetailsDialog: FC<IInterceptorDetailsDialogProps> = (props) => {
	const { open, interceptorDetails, onClose } = props;

	const { notify } = useNotification();

	const authState = useAppSelector((state) => state.auth);

	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);

	// CREATE INTERCEPTOR
	const [
		createInterceptor,
		{ isLoading: createInterceptorLoading, error: createInterceptorError, reset: resetCreateInterceptorState }
	] = useCreateInterceptorMutation();

	// EDIT INTERCEPTOR
	const [
		editInterceptor,
		{ isLoading: editInterceptorLoading, error: editInterceptorError, reset: resetEditInterceptorState }
	] = useEditInterceptorMutation();

	// DELETE INTERCEPTOR
	const [deleteInterceptor, { isLoading: deleteInterceptorLoading, reset: resetDeleteInterceptorState }] =
		useDeleteInterceptorMutation();

	const {
		reset,
		register,
		handleSubmit,
		formState: { errors, isDirty: inputChanged }
	} = useForm<IInterceptorFormInputs>();

	const readOnlyPermission = useMemo<boolean>(
		() => !authState.user.permissions || !authState.user.permissions.interceptor_vehicles?.edit,
		[authState.user.permissions]
	);

	function handleSaveButtonClick(formData: IInterceptorFormInputs) {
		if (interceptorDetails) {
			editInterceptor({
				id: interceptorDetails.id,
				data: {
					location_name: formData.location_name,
					numberplate: formData.number_plate.toUpperCase(),
					uuid: formData.uuid
				}
			})
				.unwrap()
				.then(() => onClose())
				.catch(() => {});
		} else {
			createInterceptor({
				location_name: formData.location_name,
				numberplate: formData.number_plate.toUpperCase(),
				uuid: formData.uuid
			})
				.unwrap()
				.then(() => onClose())
				.catch(() => {});
		}
	}

	function handleOpenDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(true);
	}

	function handleCloseDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(false);
	}

	function handleDeleteInterceptor() {
		if (interceptorDetails) {
			deleteInterceptor(interceptorDetails.id)
				.unwrap()
				.then(() => {
					onClose();
					handleCloseDeleteConfirmationDialog();
				})
				.catch((error) => {
					notify("data" in error && error.data ? String(error.data) : "Failed to delete interceptor", "error");
				});
		}
	}

	useEffect(() => {
		if (open) {
			reset({
				location_name: interceptorDetails?.location_name ?? "",
				number_plate: interceptorDetails?.number_plate ?? "",
				uuid: interceptorDetails?.uuid ?? ""
			});
		} else {
			resetCreateInterceptorState();
			resetEditInterceptorState();
			resetDeleteInterceptorState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [interceptorDetails, open]);

	return (
		<AGDialog
			open={open}
			onClose={onClose}
			title={interceptorDetails ? "Edit Interceptor Details" : "Add New Interceptor"}
			color="primary"
			maxWidth="xs"
		>
			<Box component="form" noValidate onSubmit={handleSubmit(handleSaveButtonClick)}>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12 }}>
						<TextField
							required
							fullWidth
							size="small"
							label="Number Plate"
							autoComplete="off"
							error={!!errors.number_plate}
							helperText={errors.number_plate?.message?.toString() || ""}
							slotProps={{ input: { readOnly: readOnlyPermission, classes: { root: "vehicle-number-input-root" } } }}
							{...register("number_plate", {
								required: "Number plate is required",
								pattern: { value: VALIDATION_REGEX.NUMBER_PLATE, message: "Invalid number plate" }
							})}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<TextField
							required
							fullWidth
							size="small"
							label="Serial Number"
							autoComplete="off"
							error={!!errors.uuid}
							helperText={errors.uuid?.message?.toString() || ""}
							slotProps={{ input: { readOnly: readOnlyPermission } }}
							{...register("uuid", {
								required: "Serial number is required",
								pattern: { value: VALIDATION_REGEX.ALPHA_NUMERIC, message: "Invalid serial number" }
							})}
						/>
					</Grid2>

					{interceptorDetails ? (
						<Grid2 size={{ xs: 12 }}>
							<TextField
								fullWidth
								size="small"
								label="Device"
								autoComplete="off"
								value={interceptorDetails.device_name}
								slotProps={{ input: { readOnly: true } }}
							/>
						</Grid2>
					) : null}

					<Grid2 size={{ xs: 12 }}>
						<TextField
							fullWidth
							size="small"
							label="Location"
							autoComplete="off"
							error={!!errors.location_name}
							helperText={errors.location_name?.message?.toString() || ""}
							slotProps={{ input: { readOnly: readOnlyPermission } }}
							{...register("location_name")}
						/>
					</Grid2>

					{createInterceptorError ? (
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
								{"data" in createInterceptorError && createInterceptorError.data
									? String(createInterceptorError.data)
									: "Failed to add new interceptor"}
							</Typography>
						</Grid2>
					) : null}

					{editInterceptorError ? (
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
								{"data" in editInterceptorError && editInterceptorError.data
									? String(editInterceptorError.data)
									: "Failed to edit interceptor"}
							</Typography>
						</Grid2>
					) : null}

					{!readOnlyPermission ? (
						<Grid2 size={{ xs: 12 }}>
							<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem" }}>
								{interceptorDetails ? (
									<LoadingButton
										variant="outlined"
										color="error"
										onClick={handleOpenDeleteConfirmationDialog}
										startIcon={<DeleteIcon />}
										loading={deleteInterceptorLoading}
										loadingPosition="start"
									>
										Delete
									</LoadingButton>
								) : null}

								<LoadingButton
									disableElevation
									variant="contained"
									color="primary"
									startIcon={<SaveIcon />}
									loading={createInterceptorLoading || editInterceptorLoading}
									loadingPosition="start"
									type="submit"
									disabled={!inputChanged}
								>
									Save
								</LoadingButton>
							</Box>
						</Grid2>
					) : null}
				</Grid2>
			</Box>

			<ConfirmationDialog
				open={showDeleteConfirmationDialog}
				title="Delete Interceptor"
				heading="Are you sure you want to delete this interceptor?"
				onClose={handleCloseDeleteConfirmationDialog}
				onConfirm={handleDeleteInterceptor}
				color="error"
				confirmButtonLoading={deleteInterceptorLoading}
				submitButtonText="Delete"
				width="xs"
			/>
		</AGDialog>
	);
};

export default InterceptorDetailsDialog;
